import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { Search, Ruler } from 'lucide-react';

interface FloorPlanViewerProps {
  floorPlan: string;
  floorPlanImages?: string[];
  apartmentName: string;
  onFullscreenOpen?: () => void;
}

const ViewerContainer = styled.div`
  width: 100%;
  margin: 16px 0;

  @media (max-width: 850px) {
    margin: 12px 0 0;
    order: 10;
  }
`;

const ViewerTitle = styled.h4`
  margin: 0 0 12px 0;
  font-size: 16px;
  font-weight: 600;
  font-family: 'Inter', sans-serif;
  color: white;
  display: flex;
  align-items: center;
  gap: 8px;
  
  @media (max-width: 850px) {
    color: #64748b;
    font-size: 13px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    margin: 0 0 8px 0;
  }
`;

const ImageContainer = styled.div`
  position: relative;
  width: 100%;
  height: 300px;
  background: rgba(248, 250, 252, 0.5);
  border-radius: 8px;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;

  @media (hover: hover) and (pointer: fine) {
    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
    }

    &:hover .fullscreen-hint {
      opacity: 1;
    }
  }

  @media (max-width: 850px) {
    height: auto;
    min-height: 160px;
    max-height: 220px;
    background: #f8fafc;
    border: 1px solid #e2e8f0;
    border-radius: 12px;
    cursor: pointer;
  }
`;

const FloorPlanImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: contain;
  display: block;
  padding: 8px;

  @media (max-width: 850px) {
    max-height: 220px;
    padding: 12px;
  }
`;

const FullscreenHint = styled.div`
  position: absolute;
  top: 12px;
  right: 12px;
  background: rgba(0, 0, 0, 0.8);
  color: white;
  padding: 8px 12px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 500;
  opacity: 0;
  transition: opacity 0.2s ease;
  z-index: 5;
  display: flex;
  align-items: center;
  gap: 6px;

  @media (max-width: 850px) {
    opacity: 1;
    background: rgba(15, 23, 42, 0.75);
    font-size: 11px;
    padding: 6px 10px;
    top: 8px;
    right: 8px;
  }
`;

const ErrorMessage = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  color: #ef4444;
  font-size: 14px;
  text-align: center;
  padding: 20px;
`;

const FloorPlanViewer: React.FC<FloorPlanViewerProps> = ({ 
  floorPlan,
  floorPlanImages = [],
  apartmentName,
  onFullscreenOpen
}) => {
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth <= 850);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const previewImage = floorPlanImages[0];
  const useImagePreview = isMobile && !!previewImage;

  const handleLoad = () => {
    setIsLoading(false);
    setHasError(false);
  };

  const handleError = () => {
    setIsLoading(false);
    setHasError(true);
  };

  const handleClick = () => {
    if (onFullscreenOpen) {
      onFullscreenOpen();
    }
  };

  if (!floorPlan && !previewImage) {
    return null;
  }

  return (
    <ViewerContainer>
      <ViewerTitle>
        <Ruler size={16} style={{ display: 'inline-block', marginRight: '8px' }} />
        Grundriss
      </ViewerTitle>
      
      <ImageContainer onClick={handleClick}>
        <FullscreenHint className="fullscreen-hint">
          <Search size={16} style={{ display: 'inline-block', marginRight: '8px' }} />
          {isMobile ? 'Tippen für Vollbild' : 'Für Vollbild klicken'}
        </FullscreenHint>

        {isLoading && !hasError && (
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            height: '100%',
            minHeight: isMobile ? '160px' : 'auto',
            flexDirection: 'column',
            gap: '16px',
            padding: '20px'
          }}>
            <p style={{ color: '#666', fontSize: '14px', textAlign: 'center' }}>
              Grundriss wird geladen...
            </p>
          </div>
        )}
        
        {hasError && (
          <ErrorMessage>
            Grundriss konnte nicht geladen werden
          </ErrorMessage>
        )}

        {useImagePreview ? (
          <FloorPlanImage
            src={previewImage}
            alt={`Grundriss für ${apartmentName}`}
            onLoad={handleLoad}
            onError={handleError}
            style={{ display: isLoading || hasError ? 'none' : 'block' }}
          />
        ) : (
          <iframe
            src={`${floorPlan}#toolbar=0&navpanes=0&scrollbar=0&view=FitH`}
            style={{
              width: '100%',
              height: '100%',
              border: 'none',
              display: isLoading || hasError ? 'none' : 'block',
              borderRadius: '8px'
            }}
            title={`Grundriss für ${apartmentName}`}
            onLoad={handleLoad}
            onError={handleError}
          />
        )}
      </ImageContainer>
    </ViewerContainer>
  );
};

export default FloorPlanViewer;
